import { ViewIds } from '@ardoq/api-types';
import { isEqual } from 'lodash';
import type { HasViewpointMode } from '@ardoq/graph';
import {
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  allWorkspacesClosed,
  AllWorkspacesClosedPayload,
  PayloadActiveMainTabLeft,
  PayloadActiveTab,
  setActiveMainTabLeft,
  setActiveMainTabRight,
} from 'streams/views/mainContent/actions';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { isViewpointModeView } from 'views/environmentals/viewSettings/utils';

export type RequestedTabs = HasViewpointMode & {
  activeTabIdLeft: ViewIds;
  activeTabIdRight: ViewIds;
};

export const DEFAULT_REQUESTED_TABS: RequestedTabs = {
  isViewpointMode: false,
  activeTabIdLeft: ViewIds.NONE,
  activeTabIdRight: ViewIds.NONE,
};

const requestLeftTab = (
  state: RequestedTabs,
  { activeTabId, isViewpointMode }: PayloadActiveMainTabLeft
): RequestedTabs => ({
  ...state,
  isViewpointMode,
  activeTabIdLeft: activeTabId,
});

const requestRightTab = (
  state: RequestedTabs,
  { activeTabId }: PayloadActiveTab
): RequestedTabs => ({ ...state, activeTabIdRight: activeTabId });

const followModeChange = (
  state: RequestedTabs,
  { isViewpointMode }: HasViewpointMode
): RequestedTabs => {
  const activeTabIdLeft =
    isViewpointMode && !isViewpointModeView(state.activeTabIdLeft)
      ? ViewIds.NONE
      : state.activeTabIdLeft;
  const activeTabIdRight = isViewpointMode
    ? ViewIds.NONE
    : state.activeTabIdRight;
  const nextState = {
    ...state,
    isViewpointMode,
    activeTabIdLeft,
    activeTabIdRight,
  };
  return isEqual(nextState, state) ? state : nextState;
};

const resetRequestedTabs = (
  _: RequestedTabs,
  { keepCurrentViewPointMode }: AllWorkspacesClosedPayload
): RequestedTabs => ({
  ...DEFAULT_REQUESTED_TABS,
  isViewpointMode: Boolean(keepCurrentViewPointMode),
});

export const requestedTabs$ = persistentReducedStream<RequestedTabs>(
  'requestedTabs$',
  DEFAULT_REQUESTED_TABS,
  [
    reducer(setActiveMainTabLeft, requestLeftTab),
    reducer(setActiveMainTabRight, requestRightTab),
    reducer(allWorkspacesClosed, resetRequestedTabs),
    streamReducer(isViewpointMode$, followModeChange),
  ]
);
